import {
  formatDateRangeShort,
  formatDateWithDayOfWeek,
  formatDateWithoutDay,
} from '@/utils';
import {
  Box,
  ChevronRightIcon,
  ChevronLeftIcon,
  Typography,
  useTheme,
  Button,
} from '@silverstein-properties/inspirelabs-ui';
import { useMemo } from 'react';
import { CalendarFilter } from '../CalendarFilter';
import { OptionType } from '../CalendarFilter/Autocomplete';
import { addDays, addMonths } from 'date-fns';
import { SxProps } from '@mui/material';
import { BUTTONS_VIEWS, VIEWS, EVENTS_FILTERS } from '@/constants';

const calendarHeaderVariants: Record<string, SxProps> = {
  default: {
    gridTemplateColumns: '350px auto 350px',
  },
  small: {
    gridTemplateColumns: 'auto auto auto',
  },
} as const;

export type CalendarHeaderVariantType = keyof typeof calendarHeaderVariants;

type CalendarHeaderProps = {
  onChangeSelectedView: (view: string) => void;
  selectedView?: string;
  onChangeSelectedFilter: (filter: string) => void;
  selectedFilter?: string;
  products?: OptionType[];
  onChangeSelectedProducts: (products: string[]) => void;
  selectedDate: Date;
  onChangeSelectedDate: (date: Date) => void;
  variant?: CalendarHeaderVariantType;
  id?: string;
};

export const CalendarHeader = ({
  onChangeSelectedView,
  selectedView,
  onChangeSelectedFilter,
  selectedFilter,
  products,
  onChangeSelectedProducts,
  selectedDate,
  onChangeSelectedDate,
  variant = 'default',
  id,
}: CalendarHeaderProps) => {
  const theme = useTheme();

  const handleChangeDate = (type: 'prev' | 'next') => {
    if (selectedView === VIEWS.DAY) {
      onChangeSelectedDate(addDays(selectedDate, type === 'prev' ? -1 : 1));
    } else if (selectedView === VIEWS.WEEK) {
      onChangeSelectedDate(addDays(selectedDate, type === 'prev' ? -7 : 7));
    } else if (selectedView === VIEWS.MONTH) {
      onChangeSelectedDate(addMonths(selectedDate, type === 'prev' ? -1 : 1));
    } else if (selectedView === VIEWS.LIST) {
      onChangeSelectedDate(addMonths(selectedDate, type === 'prev' ? -1 : 1));
    }
  };

  const formatedDate = useMemo(() => {
    if (selectedFilter === EVENTS_FILTERS.CUSTOMERS) {
      return formatDateWithoutDay(selectedDate);
    } else if (selectedView === VIEWS.DAY) {
      return formatDateWithDayOfWeek(selectedDate);
    } else if (selectedView === VIEWS.WEEK) {
      const dayOfWeek = selectedDate.getDay();
      const firstDayOfWeek = addDays(selectedDate, -dayOfWeek);
      const lastDayOfWeek = addDays(firstDayOfWeek, 6);
      return formatDateRangeShort(firstDayOfWeek, lastDayOfWeek);
    }
    return formatDateWithoutDay(selectedDate);
  }, [selectedView, selectedDate, selectedFilter]);

  const handleChangeView = (view: string) => {
    if (selectedFilter === EVENTS_FILTERS.CUSTOMERS) {
      onChangeSelectedFilter(EVENTS_FILTERS.ALL);
    }
    onChangeSelectedView(view);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: 55,
        display: 'grid',
        mb: theme.spacers.l,
        ...calendarHeaderVariants[variant],
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <ChevronLeftIcon
            width={24}
            height={24}
            onClick={() => handleChangeDate('prev')}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <ChevronRightIcon
            width={24}
            height={24}
            onClick={() => handleChangeDate('next')}
          />
        </Box>

        <Typography sx={{ ml: theme.spacers.m }} variant="h6">
          {formatedDate}
        </Typography>
      </Box>
      <CalendarFilter
        id={id}
        onChangeSelectedProducts={onChangeSelectedProducts}
        onChangeSelectedFilter={onChangeSelectedFilter}
        products={products}
        selectedFilter={selectedFilter}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          gap: theme.spacers.s,
        }}
      >
        {BUTTONS_VIEWS.map(button => (
          <Button
            key={button.label}
            variant="outlined"
            size="medium"
            onClick={() => handleChangeView(button.id)}
            sx={{
              height: 44,
              borderColor:
                selectedView === button.id &&
                selectedFilter !== EVENTS_FILTERS.CUSTOMERS
                  ? theme.palette.primary.main
                  : theme.palette.grey[400],
            }}
          >
            {button.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
};
